import { scoreColor } from "@/format";

// Lightweight dependency-free SVG charts. Deterministic rendering.

export interface RadarSeries {
  label: string;
  color: string;
  values: (number | null)[]; // 0..100 per axis
}

export function RadarChart({
  axes,
  series,
  size = 260,
}: {
  axes: string[];
  series: RadarSeries[];
  size?: number;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 34;
  const n = axes.length;
  const angle = (i: number) => (Math.PI * 2 * i) / n - Math.PI / 2;
  const point = (i: number, val: number) => {
    const rr = (val / 100) * r;
    return [cx + rr * Math.cos(angle(i)), cy + rr * Math.sin(angle(i))];
  };
  const rings = [25, 50, 75, 100];
  return (
    <svg viewBox={`0 0 ${size} ${size}`} width="100%" style={{ maxWidth: size }} role="img" aria-label="Radar chart">
      {rings.map((ring) => (
        <polygon
          key={ring}
          points={axes.map((_, i) => point(i, ring).join(",")).join(" ")}
          fill="none"
          stroke="var(--border)"
          strokeWidth={1}
        />
      ))}
      {axes.map((ax, i) => {
        const [x, y] = point(i, 100);
        const [lx, ly] = [cx + (r + 16) * Math.cos(angle(i)), cy + (r + 16) * Math.sin(angle(i))];
        return (
          <g key={ax}>
            <line x1={cx} y1={cy} x2={x} y2={y} stroke="var(--border)" strokeWidth={1} />
            <text
              x={lx}
              y={ly}
              fontSize={10}
              fill="var(--text-dim)"
              textAnchor={Math.abs(lx - cx) < 6 ? "middle" : lx > cx ? "start" : "end"}
              dominantBaseline="middle"
            >
              {ax}
            </text>
          </g>
        );
      })}
      {series.map((s) => {
        const pts = axes.map((_, i) => point(i, s.values[i] ?? 0));
        return (
          <g key={s.label}>
            <polygon
              points={pts.map((p) => p.join(",")).join(" ")}
              fill={s.color}
              fillOpacity={0.14}
              stroke={s.color}
              strokeWidth={2}
            />
            {pts.map((p, i) => (
              <circle key={i} cx={p[0]} cy={p[1]} r={s.values[i] === null ? 0 : 2.5} fill={s.color} />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

export function BarRow({ label, value, max = 100, color }: { label: string; value: number | null; max?: number; color?: string }) {
  const w = value === null ? 0 : Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <div className="row" style={{ gap: 10, margin: "3px 0" }}>
      <span className="small" style={{ width: 130, color: "var(--text-dim)" }}>{label}</span>
      <span className="scorebar grow">
        <span style={{ width: `${w}%`, background: color ?? scoreColor(value) }} />
      </span>
      <span className="small score-num" style={{ width: 40, textAlign: "right" }}>
        {value === null ? "—" : value.toFixed(0)}
      </span>
    </div>
  );
}

/** Simple color-scale cell for heatmap matrices. */
export function heatColor(t: number | null): string {
  if (t === null) return "var(--surface-2)";
  // t in 0..1 -> blend accent-weak to accent
  const clamped = Math.max(0, Math.min(1, t));
  return `color-mix(in srgb, var(--accent) ${Math.round(clamped * 82 + 8)}%, var(--surface))`;
}
